import type { RouteMeta } from "vue-router";

import type { Ziggy } from "@solvari/common-fe/integrations";
import type { LocaleMorpheus } from "@solvari/translations";

import { ziggyPathToRouterPath } from "@solvari/common-fe/integrations";

import { ziggyRouter } from "@/shared/libs/ziggy";

import type { StatamicConstraints } from "./routes.ts";

import { customRoutes, statamicRoutes } from "./routes.ts";

type Route = {
  component: () => Promise<unknown>;
  meta?: RouteMeta;
  name: string;
  path: string;
};

function buildConstraint(values: string[]): string {
  return values.filter((value) => !/[.*+?^${}()|[\]\\]/.test(value)).join("|");
}

function hasValidConstraints(route: Record<string, unknown>): boolean {
  if (!("paramConstraints" in route) || !route.paramConstraints) return true;
  return Object.values(route.paramConstraints as Record<string, string>).every(
    Boolean,
  );
}

function buildCustomServerRoutes(
  ziggy: Ziggy,
  provinceConstraint: string,
): Route[] {
  return customRoutes(provinceConstraint)
    .filter(hasValidConstraints)
    .map((route) => {
      const ziggyRoute = ziggy.routes[route.name];
      if (!ziggyRoute) {
        throw new Error(
          `Tried to get ziggy route '${route.name}' but it was not defined`,
        );
      }
      const uri =
        ziggyRoute.uri === "/"
          ? ziggyRoute.uri
          : ziggyPathToRouterPath(
              `/${ziggyRoute.uri}`,
              "paramConstraints" in route ? route.paramConstraints : undefined,
            );

      return {
        component: route.component,
        meta: "meta" in route ? route.meta : undefined,
        name: route.name,
        path: uri,
      };
    });
}

function buildCustomClientRoutes(provinceConstraint: string): Route[] {
  return customRoutes(provinceConstraint)
    .filter(hasValidConstraints)
    .map((route) => ({
      component: route.component,
      meta: "meta" in route ? route.meta : undefined,
      name: route.name,
      path: ziggyRouter(
        route.name,
        "paramConstraints" in route ? route.paramConstraints : undefined,
      ),
    }));
}

function localeToPathPrefix(locale: LocaleMorpheus): string {
  const prefixMap: Partial<Record<LocaleMorpheus, string>> = {
    "be-NL": "/nl",
    "be-FR": "/fr",
  };
  return prefixMap[locale] ?? "";
}

function buildStatamicRoutes(
  constraints: StatamicConstraints,
  locale: LocaleMorpheus,
): Route[] {
  const prefix = localeToPathPrefix(locale);

  return statamicRoutes(constraints)
    .filter(hasValidConstraints)
    .map((route) => ({
      component: route.component,
      name: route.name,
      path: ziggyPathToRouterPath(
        `${prefix}${route.path}`,
        "paramConstraints" in route
          ? (route.paramConstraints as Record<string, string>)
          : undefined,
      ),
    }));
}

export {
  buildConstraint,
  buildCustomClientRoutes,
  buildCustomServerRoutes,
  buildStatamicRoutes,
  localeToPathPrefix,
};
